import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, Route, useHistory } from 'react-router-dom';
import { SecureRoute, Security } from '@okta/okta-react';
import { OktaAuth } from '@okta/okta-auth-js';
import { ErrorBoundary, Sidebar, RequireRole } from 'components';
import { CustomLoginCallback, Devices, Login, Locations, Commands, Subscribe } from 'pages';
import { UserRole } from 'enums';
import { axiosAPI, MruService, UserService, ManufacturersService } from 'services';
import { SideNavigationPageContent, SideNavigationWrapper } from '@cfa/react-components';
import { Toaster } from 'react-hot-toast';


// Authentication via Okta
const oktaAuth = new OktaAuth({
    issuer: process.env.REACT_APP_OKTA_ISSUER,
    clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
    redirectUri: window.location.origin + '/login/callback',
});

export const oktaScopes = ['openid', 'email', 'profile'];

oktaAuth.authStateManager.subscribe((authState) => {
    const token = authState?.accessToken?.accessToken;

    if (token) {
        const headerVal = 'Bearer ' + token;
        axiosAPI.defaults.headers.common['Authorization'] = headerVal;
        UserService.parseOktaUserInfo(authState);
    }
});

export function App(props) {
    const history = useHistory();

    const onAuthRequired = () => {
        history.push('/login');
    }

    // subscribe to auth change
    useEffect(() => {
        oktaAuth.authStateManager.subscribe(onChangeAuthState);
        return () => {
            oktaAuth.authStateManager.unsubscribe(onChangeAuthState);
        }
    });

    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const onChangeAuthState = useCallback((authState) => {
        setIsAuthenticated(true);
    }, []);

    // get mrus once authenticated
    useEffect(() => {
        if (isAuthenticated) {
            MruService.getMruDevices();
            MruService.getMruLocations();
            MruService.getMruCommands();
            ManufacturersService.getManufacturers();
        }
    }, [isAuthenticated]);

    return (
        <ErrorBoundary>
            <Security oktaAuth={oktaAuth} onAuthRequired={onAuthRequired}>
                <SideNavigationWrapper>
                    <Sidebar />
                    <SideNavigationPageContent>
                        <div className="mainContainer">
                            <Route path='/login/callback' exact component={CustomLoginCallback} />
                            <Route path='/login' exact component={Login} />
                            <SecureRoute path='/devices' exact component={() => (
                                <RequireRole role={UserRole.ViewOnly}>
                                    <Devices />
                                </RequireRole>
                            )} />
                            <SecureRoute path='/' exact component={() => (
                                <RequireRole role={UserRole.ViewOnly}>
                                    <Redirect to={'/devices'} />
                                </RequireRole>
                            )} />
                            <SecureRoute path='/locations' exact component={() => (
                                <RequireRole role={UserRole.ViewOnly}>
                                    <Locations />
                                </RequireRole>
                            )} />
                            <SecureRoute path='/commands' exact component={() => (
                                <RequireRole role={UserRole.ViewOnly}>
                                    <Commands />
                                </RequireRole>
                            )} />
                            <SecureRoute path='/subscribe' exact component={() => (
                                <RequireRole role={UserRole.ViewOnly}>
                                    <Subscribe />
                                </RequireRole>
                            )} />
                        </div>
                    </SideNavigationPageContent>

                </SideNavigationWrapper>

                <Toaster />
            </Security>
        </ErrorBoundary>
    );
}
